import * as React from 'react';
import './configureWorkflowContent.scss';
import { ConfigureMainWorkflow } from './configureMainWorkflow';
import { ConfigureLightUpdateWorkflow } from './configureLightUpdateWorkflow';
import { useWorkflowModal } from './useWorkflowModal';
import { Buttons, Primary, Secondary } from 'rcd-cpp-ui/ui/button';
import { Metadata } from '../../../types/Metadata';
import {
  WorkflowSavePayload,
  WorkflowSelectionChange
} from '../../../types/ConfigureWorkflowTypes';

export interface ConfigureWorkflowContentProps {
  isLightUpdate: boolean;
  data: Partial<Metadata>;
  saveLabel?: string;
  enableSave?: boolean;
  initialWorkflowState: WorkflowSavePayload;
  onSave: (payload: WorkflowSavePayload) => Promise<void>;
  onClose: () => void;
}

export const ConfigureWorkflowContent = ({
  isLightUpdate,
  data,
  saveLabel = 'Save',
  enableSave = false,
  initialWorkflowState,
  onSave,
  onClose
}: ConfigureWorkflowContentProps) => {
  const {
    workflowState,
    onWorkflowStateChange,
    isSaving,
    isSaveDisabled,
    setIsSaveDisabled,
    errorMessage,
    setHasChanges,
    setIsModalDisabled,
    handleClose,
    ConfirmModal,
    wrapSave
  } = useWorkflowModal({ initialState: initialWorkflowState, onClose });

  const Component = isLightUpdate
    ? ConfigureLightUpdateWorkflow
    : ConfigureMainWorkflow;

  const onSaveClick = wrapSave(() => onSave(workflowState), {
    errorMessage: 'Failed to save workflow configuration. Please try again.',
    onSuccess: onClose
  });

  return (
    <div className="configure-workflow-modal-content">
      <Component
        onClose={onClose}
        data={data}
        onSave={onSave}
        workflowState={workflowState}
        onWorkflowStateChange={onWorkflowStateChange}
        onSaveDisabledChange={setIsSaveDisabled}
        errorMessage={errorMessage}
        onHasChangesChange={setHasChanges}
        onIsModalDisabledChange={setIsModalDisabled}
        isSaving={isSaving}
      />
      <Buttons>
        <Secondary label="Cancel" onClick={handleClose} />
        <Primary
          label={saveLabel}
          onClick={onSaveClick}
          disabled={!enableSave && isSaveDisabled}
        />
      </Buttons>
      <ConfirmModal />
    </div>
  );
};
